import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { axiosCacheInstance } from '../../../axiosSetup';


export const submitRefundRequest = createAsyncThunk(
    'refund/submitRefundRequest',
    async (finalPayload: any, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/api/edo-rest-v2/v2/refundRequest/submit`,
                finalPayload
            );

            if (response.data?.status === 'FAILED') {
                return rejectWithValue(response.data);
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);


export const getRefundTypeList = createAsyncThunk(
    'refund/getRefundTypeList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=REFUND_TYPE&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return response.data?.map((item: any) => ({
                value: item.codeId,
                label: item.codeDesc,
            })) ?? [];
        } catch {
            return [];
        }
    }
);


export const getRefundCountryAreaCodeList = createAsyncThunk(
    'refund/getRefundCountryAreaCodeList',
      async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=REFUND_AREA_CODE&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);


export const getRefundToList = createAsyncThunk(
    'refund/getRefundToList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=REFUND_TO&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return response.data?.map((item: any) => ({
                value: item.codeId,
                label: item.codeDesc,
            })) ?? [];
        } catch {
            return [];
        }
    }
);


export const getRefundBankList = createAsyncThunk(
    'refund/getRefundBankList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=BANK_REFUND&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return response.data?.map((item: any) => ({
                value: item.codeId,
                label: item.codeDesc,
            })) ?? [];
        } catch {
            return [];
        }
    }
);


export const getRefundCcyList = createAsyncThunk(
    'refund/getRefundCcyList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=REFUND_CURRENCY&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return response.data?.map((item: any) => ({
                value: item.codeDesc,
                label: item.codeDesc,
            })) ?? [];
        } catch {
            return [];
        }
    }
);


export const approveRejectRefund = createAsyncThunk(
    'refund/approveRejectRefund',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/api/edo-rest-v2/v2/refundRequest/approveRejectRefund`,
                payload
            );
            if (response.data?.status === 'FAILED') {
                return rejectWithValue(response.data);
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);


export const markRefundAsRefunded = createAsyncThunk(
    'refund/markRefundAsRefunded',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/api/edo-rest-v2/v2/refund/markAsRefunded`,
                payload
            );
            if (response.data?.status === 'FAILED') {
                return rejectWithValue(response.data);
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);


const mergeDocList = (
    payloadDocList: any[],
    dropdownDocList: any[],
    attachmentData: any[]
): { updatedDocList: any[] } => {
    if (!Array.isArray(payloadDocList)) payloadDocList = [];
    if (!Array.isArray(dropdownDocList) || dropdownDocList.length === 0) {
        return { updatedDocList: payloadDocList };
    }
    if (!Array.isArray(attachmentData)) attachmentData = [];

    const dropdownDocId = dropdownDocList.map((i: any) => i.id);
    const dropdownDocs = attachmentData
        .filter((i: any) => dropdownDocId.includes(i.docId))
        .map((e: any) => ({
            rowNumber: crypto.randomUUID(),
            id: e?.docId,
            name: dropdownDocList.find((i: any) => i?.id === e?.docId)?.name ?? '',
            m: true,
            isDropDownDoc: true,
        }));

    return { updatedDocList: [...payloadDocList, ...dropdownDocs] };
};

const docConfigAndAttData = (payloadDocList: any[]) => {
    if (!Array.isArray(payloadDocList) || payloadDocList.length === 0) {
        return { dropdownDocList: [], docList: [], attDataList: [] };
    }

    const updatedDocList =
        payloadDocList
            ?.filter((i: any) => i?.isMandatory != 'H' && i?.isMandatory != 'E')
            ?.map?.((i: any) => ({
                rowNumber: crypto.randomUUID(),
                id: i?.docId,
                name: i?.docName,
                m: i?.isMandatory == 'Y',
            })) ?? [];

    const dropdownOptions =
        payloadDocList
            ?.filter((i: any) => i?.isMandatory == 'H')
            ?.map?.((i: any) => ({
                id: i?.docId,
                name: i?.docName,
                m: true,
                isDropDownDoc: true,
            })) ?? [];

    const attDataList = (updatedDocList ?? [])?.map((x: any) => ({
        rowNumber: x.rowNumber,
        attachmentId: null,
        docId: x.id,
        fileName: '',
        filePath: null,
        m: x.m,
        ...(x.hasOwnProperty('isDropDownDoc') ? { isDropDownDoc: x.isDropDownDoc } : {}),
    }));

    return {
        dropdownDocList: dropdownOptions ?? [],
        docList: updatedDocList ?? [],
        attDataList,
    };
};

export const getRefundDetails = createAsyncThunk(
    'refund/getRefundDetails',
    async (payload: { refundId: any; country: string; linerId: any }, { rejectWithValue }) => {
        try {
            const docConfigResponse = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/common/attachments?countryCode=${payload.country}&linerId=${payload.linerId}&instructionType=821`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            const { dropdownDocList, docList } = docConfigAndAttData(docConfigResponse?.data);

            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/refundRequest/getRefundRequestById?refundRequestId=${payload?.refundId}&linerId=${payload.linerId}`
            );

            const data = response?.data ?? {};
            const documents =  data?.customerDocuments ?? [];
            const { updatedDocList } = mergeDocList(docList, dropdownDocList, documents);

            const visitedAttId: any[] = [];
            const attList =
                updatedDocList?.map((x: any) => {
                    const value = documents?.find(
                        (f: any) =>
                            (f?.docid) === x?.id &&
                            !visitedAttId.includes(f?.refundRequestAttId)
                    );

                    if (value?.refundRequestAttId) {
                        visitedAttId.push(value?.refundRequestAttId);
                    }

                    return {
                        rowNumber: x?.rowNumber,
                        attachmentId:value?.refundRequestAttId ?? null,
                        docId: x?.id,
                        filePath: value?.filePath || null,
                        fileName: value?.fileName || '',
                        m: x?.m,
                        isDropDownDoc: x?.isDropDownDoc,
                    };
                }) ?? [];


            return {
                next: null,
                previous: null,
                data: {
                    refundRequestId: data?.refundRequestId ?? null,
                    blId: data?.blId ?? null,
                    blNo: data?.blNo ?? '',
                    countryCode: data?.countryCode ?? '',
                    linerId: data?.linerId ?? null,
                    requestedPartnerId: data?.requestedPartnerId ?? null,
                    customerNm: data?.customerNm ?? '',
                    contactCode: data?.contactCode ?? '',
                    contactNo: data?.contactNo ?? '',
                    refundTp: data?.refundTp ?? '',
                    refundTo: data?.refundTo ?? '',
                    userRemarks: data?.userRemarks ?? '',
                    deductionAmt: data?.deductionAmt ?? null,
                    deductionAmtCurr: data?.deductionAmtCurr ?? '',
                    refundAmt: data?.refundAmt ?? null,
                    refundAmtCurr: data?.refundAmtCurr ?? '',
                    approverRemarks: data?.approverRemarks ?? '',
                    linerRemarks: data?.approverRemarks ?? '',
                    accountHolderName: data?.accountHolderName ?? '',
                    bankName: data?.bankName ?? '',
                    bankAccNo: data?.bankAccNo ?? '',
                    swiftCode: data?.swiftCode ?? '',
                    status: data?.status ?? null,
                    statusLabel: data?.statusLabel ?? '',
                    groupLevel: data?.groupLevel ?? 0,
                    customerDocuments: data?.customerDocuments ?? [],
                    attList,
                    updatedDocListData: updatedDocList,
                    dropdownDocList,
                },
            };
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);


export const getRefundCustomerAttConf = createAsyncThunk(
    'refund/getRefundCustomerAttConf',
    async (payload: { country: string; liner: any }) => {
        try {
            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/common/attachments?countryCode=${payload.country}&linerId=${payload.liner}&instructionType=821`
            );
            return response.data;
        } catch {
            return [];
        }
    }
);



export const getRefundLinerAttConf = createAsyncThunk(
    'refund/getRefundLinerAttConf',
    async (payload: { country: string; liner: any }) => {
        try {
            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/common/attachments?countryCode=${payload.country}&linerId=${payload.liner}&instructionType=821`
            );
            return response.data;
        } catch {
            return [];
        }
    }
);
